// Lazy-load analytics libraries to reduce initial bundle size
let posthogPromise = null;
// let mixpanelPromise = null; // Commented out - Mixpanel not being used
let analyticsInitialized = false;

// Lazy load PostHog
const loadPostHog = async () => {
  if (!posthogPromise) {
    posthogPromise = import("posthog-js").then((module) => module.default);
  }
  return posthogPromise;
};

// Lazy load Mixpanel - COMMENTED OUT (not being used)
// const loadMixpanel = async () => {
//   if (!mixpanelPromise) {
//     mixpanelPromise = import("mixpanel-browser").then((module) => module.default);
//   }
//   return mixpanelPromise;
// };

export const initAnalytics = async () => {
  if (analyticsInitialized) return;
  if (typeof window === "undefined") return;

  // Mixpanel commented out - not being used
  // No analytics initialization needed since Mixpanel is disabled
  // PostHog is loaded separately via script tag or lazy-loaded when needed
  
  analyticsInitialized = true;
};

// Commented out - pageview tracking now handled per-page for homepage and /cricket route
// export const trackPageView = () => {
//   if (typeof window === "undefined") return;

//   // ensure initialized before capturing
//   initAnalytics();

//   const pagePath = window.location.pathname;
//   const pageName = pagePath.split("/").pop() || "homepage";

//   const urlParams = new URLSearchParams(window.location.search);
//   const utmSource = urlParams.get("utm_source") || "organic";
//   const utmMedium = urlParams.get("utm_medium") || "website";

//   try {
//     posthog.capture("Page Viewed", {
//       page_name: pageName,
//       page_url: window.location.href,
//       utm_source: utmSource,
//       utm_medium: utmMedium,
//     });
//   } catch {
//     // no-op: avoid console noise in production
//   }
// };

/**
 * Track PostHog pageview for specific routes (homepage and /cricket)
 * Uses window.posthog if available (loaded via script tag)
 * Deferred to reduce main-thread work
 */
// Track if PostHog pageview has been sent for current pathname to prevent duplicate requests
let lastTrackedPathname = null;

export const trackPostHogPageView = async () => {
  if (typeof window === "undefined") return;
  
  const currentPathname = window.location.pathname;
  
  // Prevent duplicate pageview tracking for the same pathname
  // But allow tracking when pathname changes (route navigation)
  if (lastTrackedPathname === currentPathname) return;
  lastTrackedPathname = currentPathname;

  // Defer PostHog tracking to reduce main-thread work
  const track = async () => {
    try {
      // Wait for PostHog to be available (if loaded via GTM)
      // GTM loads PostHog after 4 seconds, so wait up to 5 seconds
      let retries = 0;
      const maxRetries = 25; // 25 * 200ms = 5 seconds max wait
      
      while (!window.posthog && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
        retries++;
      }
      
      // Check if PostHog is available via script tag (GTM)
      if (window.posthog && typeof window.posthog.capture === "function") {
        // PostHog loaded via GTM - send pageview with custom properties
        // The duplicate prevention flag ensures we only call this once per page
        const pagePath = window.location.pathname;
        const pageName = pagePath === "/" ? "homepage" : pagePath.split("/").pop() || "unknown";
        const urlParams = new URLSearchParams(window.location.search);
        const utmSource = urlParams.get("utm_source") || "organic";
        const utmMedium = urlParams.get("utm_medium") || "website";

        window.posthog.capture("$pageview", {
          page_name: pageName,
          page_url: window.location.href,
          page_path: pagePath,
          utm_source: utmSource,
          utm_medium: utmMedium,
        });
        return;
      }

      // Fallback: lazy-load posthog-js if script tag not available
      const posthog = await loadPostHog();
      if (posthog && typeof posthog.capture === "function") {
        const pagePath = window.location.pathname;
        const pageName = pagePath === "/" ? "homepage" : pagePath.split("/").pop() || "unknown";
        
        const urlParams = new URLSearchParams(window.location.search);
        const utmSource = urlParams.get("utm_source") || "organic";
        const utmMedium = urlParams.get("utm_medium") || "website";

        posthog.capture("$pageview", {
          page_name: pageName,
          page_url: window.location.href,
          page_path: pagePath,
          utm_source: utmSource,
          utm_medium: utmMedium,
        });
      }
    } catch {
      // no-op: avoid console noise in production
    }
  };

  // Use requestIdleCallback to defer tracking significantly
  // This prevents PostHog collector from blocking critical path
  // Delay longer to ensure GTM's PostHog loads first
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => {
      setTimeout(() => {
        track().catch(() => {}); // Handle async properly
      }, 5000); // Delay 5 seconds after idle - after GTM loads (4 seconds)
    }, { timeout: 6000 });
  } else {
    // Fallback: delay after mount
    setTimeout(() => {
      track().catch(() => {}); // Handle async properly
    }, 6000);
  }
};

/**
 * Track CTA button clicks
 * @param {Event} event - The click event
 * @param {string} ctaType - Type of CTA (e.g., 'apply_now', 'buy_now', 'download_brochure')
 * @param {object} additionalProps - Any additional properties to track
 */
export const trackCTAClick = async (event, ctaType, additionalProps = {}) => {
  if (typeof window === "undefined") return;

  await initAnalytics();

  const ctaText = event?.currentTarget?.innerText?.trim() || "";

  try {
    // Prefer window.posthog (script tag) for faster access
    const ph = window.posthog || await loadPostHog();
    if (ph && typeof ph.capture === "function") {
      ph.capture("Website CTA Clicked", {
        cta_text: ctaText,
        cta_type: ctaType,
        page_url: window.location.href,
        page_path: window.location.pathname,
        ...additionalProps,
      });
    }
  } catch {
    // no-op
  }
};

/**
 * Track generic events
 * @param {string} eventName - Name of the event
 * @param {object} properties - Event properties
 */
export const trackEvent = async (eventName, properties = {}) => {
  if (typeof window === "undefined") return;

  await initAnalytics();

  try {
    const ph = window.posthog || await loadPostHog();
    if (ph && typeof ph.capture === "function") {
      ph.capture(eventName, {
        page_url: window.location.href,
        page_path: window.location.pathname,
        ...properties,
      });
    }
  } catch {
    // no-op
  }
};

/**
 * Track form submissions
 * @param {string} formType - Type of form (e.g., 'fellowship_lead', 'contact_form')
 * @param {object} formData - Form data to track
 */
export const trackFormSubmission = async (formType, formData = {}) => {
  if (typeof window === "undefined") return;

  await initAnalytics();

  try {
    const ph = window.posthog || await loadPostHog();
    if (ph && typeof ph.capture === "function") {
      ph.capture(`${formType}_submission`, {
        ...formData,
        page_url: window.location.href,
        page_path: window.location.pathname,
      });
    }
  } catch {
    // no-op
  }
};

export default {
  initAnalytics,
  // trackPageView,
  trackCTAClick,
  trackEvent,
  trackFormSubmission,
};
